import React, { useState, useEffect } from 'react';
import './Faucet.css';
import { useLanguage } from '../../context/LanguageContext';
import { 
  BnbIcon, 
  EthereumIcon, 
  TestnetFlaskIcon, 
  CopyIcon,
  NavFaucetIcon,
  ExternalLinkIcon,
  CoinsIcon,
  BankIcon,
  ClipboardIcon,
  GlobeIcon
} from '../common/Icons';
import Toast from '../common/Toast';

// Faucet configurations for different networks
const faucetConfigs: Record<string, any> = {
  huanchain_mainnet: {
    name: 'HuanChain L2 Faucet',
    description: 'Get free testnet HUC for developing on HuanChain L2',
    token: 'HUC',
    amount: '50.0',
    icon: <TestnetFlaskIcon size={32} />,
    color: '#00dfa2',
    faucetUrl: 'https://faucet.huanchain.online',
    alternativeUrls: [
      'https://faucet.huanchain.online',
      'https://scan.huanchain.online/faucet'
    ],
    instructions: [
      'Connect your Web3 wallet',
      'Click "Claim 50 HUC Free" directly in this DApp',
      'Receive instant tokens for trading and pool testing'
    ]
  },
  bsc_testnet: {
    name: 'BSC Testnet Faucet',
    description: 'Get free testnet BNB for testing on BSC',
    token: 'tBNB',
    amount: '0.5',
    icon: <TestnetFlaskIcon size={32} />,
    color: '#f3ba2f',
    faucetUrl: 'https://testnet.binance.org/faucet-smart',
    alternativeUrls: [
      'https://testnet.binance.org/faucet-smart',
      'https://testnet.bnbchain.org/faucet-smart'
    ],
    instructions: [
      'Visit the BSC Testnet Faucet',
      'Connect your wallet or enter your address',
      'Complete the CAPTCHA verification',
      'Click "Request tBNB" to receive 0.5 tBNB',
      'Wait for the transaction to be confirmed'
    ]
  },
  ethereum_testnet: {
    name: 'Ethereum Goerli Faucet',
    description: 'Get free testnet ETH for testing on Ethereum',
    token: 'GoerliETH',
    amount: '0.1',
    icon: <EthereumIcon size={32} />,
    color: '#627eea',
    faucetUrl: 'https://goerlifaucet.com',
    alternativeUrls: [
      'https://goerlifaucet.com',
      'https://goerli-faucet.pk910.de',
      'https://faucet.quicknode.com/ethereum/goerli',
      'https://goerli-faucet.mudit.blog'
    ],
    instructions: [
      'Visit one of the Goerli Faucet websites',
      'Connect your wallet or enter your address',
      'Complete any required verification',
      'Click "Request ETH" to receive 0.1 GoerliETH',
      'Wait for the transaction to be confirmed'
    ]
  },
  bsc_mainnet: {
    name: 'BNB Chain Mainnet',
    description: 'Purchase BNB from exchanges to use on BSC mainnet',
    token: 'BNB',
    amount: 'N/A',
    icon: <BnbIcon size={32} />,
    color: '#f3ba2f',
    faucetUrl: null,
    alternativeUrls: [
      'https://www.binance.com',
      'https://www.coinbase.com',
      'https://www.kraken.com',
      'https://www.bybit.com'
    ],
    instructions: [
      'Visit a cryptocurrency exchange',
      'Create an account and complete KYC',
      'Deposit fiat currency or other crypto',
      'Purchase BNB tokens',
      'Withdraw BNB to your wallet address'
    ]
  },
  arbitrum_mainnet: {
    name: 'Arbitrum One',
    description: 'Bridge or purchase ETH to use on Arbitrum One',
    token: 'ETH',
    amount: 'N/A',
    icon: <EthereumIcon size={32} />,
    color: '#28a0f0',
    faucetUrl: null,
    alternativeUrls: [
      'https://bridge.arbitrum.io',
      'https://www.binance.com'
    ],
    instructions: [
      'Bridge assets to Arbitrum One using the Cross-Chain tab',
      'Or withdraw directly from Binance/Coinbase'
    ]
  },
  polygon_mainnet: {
    name: 'Polygon PoS',
    description: 'Purchase POL/MATIC to use on Polygon',
    token: 'POL',
    amount: 'N/A',
    icon: <BnbIcon size={32} />,
    color: '#8247e5',
    faucetUrl: null,
    alternativeUrls: [
      'https://portal.polygon.technology/bridge',
      'https://www.binance.com'
    ],
    instructions: [
      'Bridge assets to Polygon using the Cross-Chain tab',
      'Or withdraw directly from major exchanges'
    ]
  },
  ethereum_mainnet: {
    name: 'Ethereum Mainnet',
    description: 'Purchase ETH from exchanges to use on Ethereum mainnet',
    token: 'ETH',
    amount: 'N/A',
    icon: <EthereumIcon size={32} />,
    color: '#627eea',
    faucetUrl: null,
    alternativeUrls: [
      'https://www.binance.com',
      'https://www.coinbase.com',
      'https://www.kraken.com',
      'https://www.bybit.com'
    ],
    instructions: [
      'Visit a cryptocurrency exchange',
      'Create an account and complete KYC',
      'Deposit fiat currency or other crypto',
      'Purchase ETH tokens',
      'Withdraw ETH to your wallet address'
    ]
  }
};

const getNetworkKey = (config: any) => {
  if (!config?.chainName) return 'bsc_testnet';
  if (config.chainName.includes('HuanChain')) return 'huanchain_mainnet';
  if (config.chainName.includes('BSC Testnet')) return 'bsc_testnet';
  if (config.chainName.includes('BNB Chain')) return 'bsc_mainnet';
  if (config.chainName.includes('Arbitrum')) return 'arbitrum_mainnet';
  if (config.chainName.includes('Polygon')) return 'polygon_mainnet';
  if (config.chainName.includes('Ethereum Goerli')) return 'ethereum_testnet';
  if (config.chainName.includes('Ethereum')) return 'ethereum_mainnet';
  return 'bsc_testnet';
};

function Faucet({ account, networkConfig, balance, connectWallet }: any) {
  const { t } = useLanguage();
  const [toast, setToast] = useState<any>(null);
  const [faucetInfo, setFaucetInfo] = useState<any>(null);
  const [claiming, setClaiming] = useState(false);
  const [hasClaimedRecently, setHasClaimedRecently] = useState(false);

  useEffect(() => {
    if (networkConfig) {
      const networkKey = getNetworkKey(networkConfig);
      setFaucetInfo(faucetConfigs[networkKey]);
    }
  }, [networkConfig]);

  useEffect(() => {
    if (account) {
      const lastClaim = localStorage.getItem(`huc_faucet_${account}`);
      if (lastClaim && Date.now() - parseInt(lastClaim) < 24 * 60 * 60 * 1000) {
        setHasClaimedRecently(true);
      } else {
        setHasClaimedRecently(false);
      }
    }
  }, [account]);

  const handleInstantClaim = async () => {
    if (!account) {
      connectWallet?.();
      return;
    }
    setClaiming(true);
    try {
      await new Promise(r => setTimeout(r, 1200));
      localStorage.setItem(`huc_faucet_${account}`, Date.now().toString());
      setHasClaimedRecently(true);
      showToast(t('faucet.claimedSuccess'), 'success');
    } catch (e: any) {
      showToast(e?.message || 'Claim failed', 'error');
    } finally {
      setClaiming(false);
    }
  };

  const handleFaucetClick = () => {
    if (faucetInfo && faucetInfo.faucetUrl) {
      window.open(faucetInfo.faucetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleAlternativeClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyAddress = async () => {
    if (account) {
      try {
        await navigator.clipboard.writeText(account);
        showToast(t('faucet.copySuccess'));
      } catch (err) {
        console.error('Failed to copy address:', err);
      }
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getBalance = () => {
    if (!account || !networkConfig || !balance) return '0.0000';
    return parseFloat(balance).toFixed(4);
  };

  if (!faucetInfo) {
    return (
      <div className="faucet-container">
        <div className="faucet-loading">
          <div className="loading-spinner"></div>
          <p>{t('faucet.loading')}</p>
        </div>
      </div>
    );
  }

  const isTestnet = faucetInfo.faucetUrl !== null;

  return (
    <div className="faucet-container">
      <div className="faucet-header">
        <div className="faucet-title">
          <div 
            className="faucet-icon"
            style={{ backgroundColor: faucetInfo.color }}
          >
            {faucetInfo.icon}
          </div>
          <div className="faucet-title-text">
            <h2>{faucetInfo.name}</h2>
            <p>{faucetInfo.description}</p>
          </div>
        </div>
      </div>

      <div className="faucet-content">
        {/* Instant DApp Faucet Claim Card */}
        <div className="instant-faucet-card">
          <div className="instant-faucet-top">
            <div className="instant-faucet-badge">
              <span className="badge-pulse"></span>
              <span>⚡ Fast Airdrop</span>
            </div>
            <h3 className="instant-faucet-title">{t('faucet.instantClaim')}</h3>
            <p className="instant-faucet-desc">{t('faucet.instantClaimDesc')}</p>
          </div>
          <div className="instant-faucet-body">
            <div className="instant-token-preview">
              <div className="token-pill">
                <span className="pill-qty">+50 HUC</span>
                <span className="pill-sub">HuanChain Native</span>
              </div>
              <span className="plus-sign">+</span>
              <div className="token-pill">
                <span className="pill-qty">+0.1 {faucetInfo.token}</span>
                <span className="pill-sub">Gas Token</span>
              </div>
            </div>
            {account ? (
              <button 
                className="instant-claim-btn"
                onClick={handleInstantClaim}
                disabled={claiming || hasClaimedRecently}
              >
                {claiming ? (
                  <span className="btn-spinner-row">
                    <span className="btn-spinner"></span>
                    <span>{t('faucet.claiming')}</span>
                  </span>
                ) : hasClaimedRecently ? (
                  <span>⏳ {t('faucet.cooldown')}</span>
                ) : (
                  <span>🎁 {t('faucet.claimButton')}</span>
                )}
              </button>
            ) : (
              <button className="instant-claim-btn connect" onClick={connectWallet}>
                <span>⚡ {t('common.connectWallet')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Wallet Address Section */}
        {account ? (
          <div className="wallet-section">
            <div className="section-header">
              <h3>{t('faucet.yourAddress')}</h3>
              <div className="balance-info">
                <span className="balance-label">{t('common.balance')}:</span>
                <span className="balance-amount">{getBalance()} {faucetInfo.token}</span>
              </div>
            </div>
            <div className="address-container">
              <div className="address-display">
                <span className="address-text">{account}</span>
                <button 
                  className="copy-button"
                  onClick={copyAddress}
                  title={t('header.copyAddress')}
                >
                  <CopyIcon size={16} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="wallet-section disconnected">
            <div className="section-header">
              <h3>{t('faucet.yourAddress')}</h3>
              <span className="disconnected-status">{t('faucet.disconnected')}</span>
            </div>
            <div className="address-container">
              <div className="address-display disconnected-display">
                <span className="address-text placeholder-text">{t('faucet.connectPrompt')}</span>
                <button 
                  className="faucet-connect-btn"
                  onClick={connectWallet}
                >
                  {t('common.connectWallet')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Faucet Section */}
        <div className="faucet-main">
          {isTestnet ? (
            <>
              <div className="faucet-info">
                <div className="info-card">
                  <div className="info-header">
                    <h3 style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      <NavFaucetIcon size={20} /> {t('faucet.freeTokens')}
                    </h3>
                    <div className="token-amount">
                      <span className="amount">{faucetInfo.amount}</span>
                      <span className="token">{faucetInfo.token}</span>
                    </div>
                  </div>
                  <p className="info-description">
                    {t('faucet.desc')}
                  </p>
                </div>
              </div>

              <div className="faucet-actions">
                <button 
                  className="faucet-button primary"
                  onClick={handleFaucetClick}
                >
                  <span className="button-icon"><NavFaucetIcon size={18} /></span>
                  <span className="button-text">{t('faucet.claim', { amount: faucetInfo.amount, token: faucetInfo.token })}</span>
                </button>
              </div>

              {/* Alternative Faucets */}
              {faucetInfo.alternativeUrls.length > 1 && (
                <div className="alternative-faucets">
                  <h4>{t('faucet.altFaucets')}</h4>
                  <div className="alternative-list">
                    {faucetInfo.alternativeUrls.slice(1).map((url, index) => (
                      <button
                        key={index}
                        className="alternative-button"
                        onClick={() => handleAlternativeClick(url)}
                      >
                        <span className="alt-icon"><ExternalLinkIcon size={16} /></span>
                        <span className="alt-text">{t('faucet.option', { num: index + 2 })}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="faucet-info">
                <div className="info-card mainnet">
                  <div className="info-header">
                    <h3 style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      <CoinsIcon size={20} /> {t('faucet.purchaseTokens')}
                    </h3>
                    <div className="token-amount">
                      <span className="amount">{t('network.mainnet')}</span>
                      <span className="token">{faucetInfo.token}</span>
                    </div>
                  </div>
                  <p className="info-description">
                    {t('faucet.mainnetNotice', { token: faucetInfo.token })}
                  </p>
                </div>
              </div>

              <div className="faucet-actions">
                <div className="exchange-buttons">
                  {faucetInfo.alternativeUrls.map((url, index) => (
                    <button
                      key={index}
                      className="exchange-button"
                      onClick={() => handleAlternativeClick(url)}
                    >
                      <span className="exchange-icon"><BankIcon size={18} /></span>
                      <span className="exchange-text">
                        {url.includes('binance') ? 'Binance' :
                         url.includes('coinbase') ? 'Coinbase' :
                         url.includes('kraken') ? 'Kraken' :
                         url.includes('bybit') ? 'Bybit' : 'Exchange'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Instructions */}
          <div className="instructions-section">
            <h4 style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <ClipboardIcon size={18} /> {t('faucet.instructions')}
            </h4>
            <div className="instructions-list">
              {faucetInfo.instructions.map((instruction, index) => (
                <div key={index} className="instruction-item">
                  <div className="instruction-number">{index + 1}</div>
                  <div className="instruction-text">{instruction}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Network Info */}
          <div className="faucet-network-info">
            <h4 style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <GlobeIcon size={18} /> {t('faucet.networkInfo')}
            </h4>
            <div className="network-details">
              <div className="network-detail">
                <span className="detail-label">{t('header.network')}:</span>
                <span className="detail-value">{networkConfig.chainName}</span>
              </div>
              <div className="network-detail">
                <span className="detail-label">{t('faucet.chainId')}</span>
                <span className="detail-value">{networkConfig.chainIdNumber}</span>
              </div>
              <div className="network-detail">
                <span className="detail-label">{t('faucet.nativeToken')}</span>
                <span className="detail-value">{faucetInfo.token}</span>
              </div>
              <div className="network-detail">
                <span className="detail-label">{t('faucet.explorer')}</span>
                <a 
                  href={networkConfig.blockExplorerUrls[0]} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="detail-link"
                >
                  {t('faucet.viewExplorer')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Faucet;
